import { useState, useEffect, useRef } from 'react'
import { Search, X, Plus, Check } from 'lucide-react'

interface Service {
  id: string
  name: string
  category: string
  default_price: number
  duration: number
}

interface ServiceSelectorProps {
  selectedServices: string[]
  onChange: (services: string[], totalDuration: number) => void
  onClose: () => void
}

const API_URL = (import.meta.env.VITE_API_URL as string) || 'https://salonflow-api.empty-mud-1234.workers.dev'

// Service categories with icons
const CATEGORY_ICONS: Record<string, string> = {
  'Cutting': '✂️',
  'Mens': '👨',
  'Colour': '🎨',
  'Highlights': '✨',
  'Styling': '💇',
  'Treatments': '💆',
  'Extensions': '💁',
  'Spa': '🧖',
}

export function ServiceSelector({ selectedServices, onChange, onClose }: ServiceSelectorProps) {
  const [services, setServices] = useState<Service[]>([])
  const [search, setSearch] = useState('')
  const [activeCategory, setActiveCategory] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onClose])

  useEffect(() => {
    setIsLoading(true)
    fetch(`${API_URL}/api/services`)
      .then(res => res.json())
      .then(data => {
        setServices(data.services || [])
        setIsLoading(false)
      })
      .catch(err => {
        console.error('Failed to load services:', err)
        setIsLoading(false)
      })
  }, [])

  // Group services by category
  const categories = [...new Set(services.map(s => s.category))].sort()

  // Filter services
  const filteredServices = services.filter(service => {
    const matchesSearch = search === '' || 
      service.name.toLowerCase().includes(search.toLowerCase()) ||
      service.category.toLowerCase().includes(search.toLowerCase())
    const matchesCategory = !activeCategory || service.category === activeCategory
    return matchesSearch && matchesCategory
  })

  // Helper to find service by name (case-insensitive, fuzzy match)
  const findServiceByName = (name: string): Service | undefined => {
    const lowerName = name.toLowerCase().trim()
    // Try exact match first
    let found = services.find(s => s.name.toLowerCase() === lowerName)
    // Try partial match if no exact match
    if (!found) {
      found = services.find(s => s.name.toLowerCase().includes(lowerName) || lowerName.includes(s.name.toLowerCase()))
    }
    return found
  }

  const toggleService = (serviceName: string) => {
    let newServices: string[]
    
    if (selectedServices.includes(serviceName)) {
      newServices = selectedServices.filter(s => s !== serviceName)
    } else {
      newServices = [...selectedServices, serviceName]
    }
    
    // Calculate new total duration (case-insensitive matching)
    const newDuration = newServices
      .map(name => findServiceByName(name))
      .filter(Boolean)
      .reduce((sum, s) => sum + (s?.duration || 60), 0)
    
    onChange(newServices, newDuration)
  }

  const getSelectedServiceDetails = () => {
    return selectedServices.map(name => findServiceByName(name)).filter(Boolean) as Service[]
  }

  const totalPrice = getSelectedServiceDetails().reduce((sum, s) => sum + s.default_price, 0)
  const totalDuration = getSelectedServiceDetails().reduce((sum, s) => sum + s.duration, 0)

  return (
    <div 
      ref={ref}
      className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-xl border border-gray-200 z-50 w-96 max-h-[400px] flex flex-col"
    >
      {/* Search */}
      <div className="p-3 border-b">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search services..."
            className="w-full pl-9 pr-4 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoFocus
          />
        </div>
      </div>

      {/* Category Pills */}
      <div className="px-3 py-2 flex gap-1.5 overflow-x-auto border-b flex-shrink-0">
        <button
          onClick={() => setActiveCategory(null)}
          className={`px-2.5 py-1 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
            !activeCategory ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          All
        </button>
        {categories.map(cat => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat === activeCategory ? null : cat)}
            className={`px-2.5 py-1 text-xs font-medium rounded-full whitespace-nowrap transition-colors flex items-center gap-1 ${
              activeCategory === cat ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <span>{CATEGORY_ICONS[cat] || '📋'}</span>
            {cat}
          </button>
        ))}
      </div>

      {/* Selected Services Summary */}
      {selectedServices.length > 0 && (
        <div className="px-3 py-2 bg-blue-50 border-b flex-shrink-0">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-blue-700">
              {selectedServices.length} service{selectedServices.length > 1 ? 's' : ''} selected
            </span>
            <span className="text-sm font-bold text-blue-700">
              £{totalPrice} · {totalDuration} mins
            </span>
          </div>
          <div className="flex flex-wrap gap-1">
            {selectedServices.map(name => (
              <span 
                key={name}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-white rounded text-xs text-blue-700 border border-blue-200"
              >
                {name}
                <button 
                  onClick={() => toggleService(name)}
                  className="hover:text-red-500"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Services List */}
      <div className="flex-1 overflow-y-auto p-2">
        {isLoading ? (
          <div className="text-center py-8 text-gray-500 text-sm">Loading services...</div>
        ) : filteredServices.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">No services found</div>
        ) : (
          <div className="space-y-1">
            {filteredServices.map(service => {
              const isSelected = selectedServices.includes(service.name)
              return (
                <button
                  key={service.id}
                  onClick={() => toggleService(service.name)}
                  className={`
                    w-full flex items-center justify-between p-2.5 rounded-lg text-left transition-all
                    ${isSelected 
                      ? 'bg-blue-50 border-2 border-blue-500' 
                      : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'}
                  `}
                >
                  <div className="flex items-center gap-2">
                    <div className={`
                      w-5 h-5 rounded flex items-center justify-center
                      ${isSelected ? 'bg-blue-600' : 'bg-gray-200'}
                    `}>
                      {isSelected ? (
                        <Check className="w-3 h-3 text-white" />
                      ) : (
                        <Plus className="w-3 h-3 text-gray-500" />
                      )}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">{service.name}</div>
                      <div className="text-xs text-gray-500">{service.duration} mins</div>
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-gray-700">
                    £{service.default_price}
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* Done Button */}
      <div className="p-3 border-t bg-gray-50">
        <button
          onClick={onClose}
          className="w-full py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
        >
          Done
        </button>
      </div>
    </div>
  )
}
